'use client';

import { motion, useReducedMotion } from 'framer-motion';
import Image from 'next/image';

export interface StatItem {
  /** 大字文案；传了 image 时作为图片 alt 与无障碍文案。 */
  value: string;
  label: string;
  /** 大字改用图片徽章（如 10s / 4K / ∞ 月桂环）；不传则渲染 value 文字。 */
  image?: string;
}

interface StatBarProps {
  stats: StatItem[];
  className?: string;
}

const itemVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: 0.5 + i * 0.12,
      duration: 0.7,
      ease: [0.22, 1, 0.36, 1] as const,
    },
  }),
};

/**
 * 量化能力条：一排图片徽章（或大数字）+ 小标签，每项内部居中对齐，
 * 随视口进入依次浮现。
 */
export const StatBar = ({ stats, className }: StatBarProps) => {
  const prefersReducedMotion = useReducedMotion();

  return (
    <div className={className}>
      <div className="flex flex-wrap items-center justify-start gap-x-10 gap-y-6 sm:gap-x-4 sm:gap-y-3">
        {stats.map((stat, i) => (
          <motion.div
            key={stat.label}
            className="flex items-center gap-10 sm:gap-4"
            initial={prefersReducedMotion ? undefined : 'hidden'}
            whileInView={prefersReducedMotion ? undefined : 'visible'}
            viewport={{ once: true, amount: 0.6 }}
            variants={itemVariants}
            custom={i}
          >
            <div className="flex flex-col items-center text-center">
              {stat.image ? (
                <Image
                  src={stat.image}
                  alt={stat.value}
                  width={212}
                  height={147}
                  className="h-[72px] w-auto sm:h-[44px] lg:h-[64px]"
                  loading="lazy"
                  decoding="async"
                />
              ) : (
                <span className="font-semibold text-4xl text-white leading-none sm:text-2xl lg:text-3xl">
                  {stat.value}
                </span>
              )}
              <span className="mt-2 text-sm text-white/60 sm:mt-1 sm:text-[11px]">{stat.label}</span>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};
